import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import {
  ArrowRight, ChevronLeft, Sparkles, Calendar, Users, Info, Clock,
} from "lucide-react";
import api from "../lib/api";
import { trackEvent } from "../lib/tracking";

/**
 * iter-108 · OfferDetail — editorial presentation page.
 *
 * Layout: banner image at top (natural ratio preserved via object-contain
 * on a fixed aspect box) then all editorial content in a clean vertical
 * flow below the image. No text is ever overlaid on the cover.
 */

// Curated fallback images per offer id (Beach Club branded — client-provided).
const FALLBACK_IMAGE = {
  pass_day:
    "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/z00iovry_Image%20site%20beach%20club-02.webp",
  sunset:
    "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/r3dvpdje_Image%20site%20beach%20club-03.webp",
  sunset_club:
    "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/r3dvpdje_Image%20site%20beach%20club-03.webp",
  brunch:
    "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/c04z1595_Image%20site%20beach%20club_Plan%20de%20travail%201.webp",
};

// Curated useful-info blurb per offer id. Each row is later dispatched to the
// matching editorial block (Horaires, Inclus, Conditions, etc.).
const USEFUL_INFO = {
  pass_day: [
    { label: "Horaires", value: "Départ toutes les 2 heures — 10 H → 20 H" },
    { label: "Inclus", value: "Traversée A/R lagune · Tapas · Cocktail signature" },
    { label: "Jours", value: "Du lundi au vendredi" },
    { label: "Conditions", value: "Réservation flexible · Confirmation immédiate" },
  ],
  sunset: [
    { label: "Horaires", value: "Départ 17 H — retour 21 H" },
    { label: "Inclus", value: "Traversée A/R · Coucher de soleil face lagune · Cocktail" },
    { label: "Jours", value: "Samedi" },
    { label: "Conditions", value: "Tenue chic recommandée · À partir de 18 ans" },
  ],
  sunset_club: [
    { label: "Horaires", value: "Every Saturday · 15:00 – 22:00" },
    { label: "Inclus", value: "Traversée A/R lagune · DJ set · Ambiance chill · Champagne à la carte" },
    { label: "Tarifs", value: "Ladies : Gratuit · Gentlemen : 25 000 FCFA" },
    { label: "Paiement", value: "100 % en ligne · Aucun cash · Aucun acompte" },
  ],
  brunch: [
    { label: "Horaires", value: "Dimanche — 11 H à 16 H" },
    { label: "Inclus", value: "Traversée A/R · Brunch buffet d'exception · Vue lagune" },
    { label: "Jours", value: "Dimanche uniquement" },
    { label: "Conditions", value: "Réservation conseillée · Enfants à demi-tarif" },
  ],
};

// Groups useful-info rows into 3 editorial blocks (Infos utiles, Inclus,
// Conditions). Anything unmatched falls back to the "Informations utiles"
// bucket so no data is ever silently dropped.
function groupUsefulInfo(rows) {
  const buckets = { info: [], inclus: [], conditions: [] };
  for (const row of rows || []) {
    const label = (row.label || "").toLowerCase();
    if (label.includes("inclus")) buckets.inclus.push(row);
    else if (label.includes("condition")) buckets.conditions.push(row);
    else buckets.info.push(row);
  }
  return buckets;
}

const fmtXOF = (n) =>
  typeof n === "number"
    ? `${new Intl.NumberFormat("fr-FR").format(n)} FCFA`
    : "";

const fmtDateFR = (iso) => {
  if (!iso) return "";
  try {
    return format(parseISO(iso), "EEEE d MMMM yyyy", { locale: frLocale });
  } catch {
    return iso;
  }
};

function SectionBlock({ label, icon, children, testid }) {
  return (
    <section className="border-t border-[#0A0A0A]/10 pt-8 md:pt-10" data-testid={testid}>
      <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#B8922A] mb-4 inline-flex items-center gap-1.5">
        {icon} {label}
      </div>
      {children}
    </section>
  );
}

export default function OfferDetail() {
  const { offerId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dateFromUrl = params.get("date") || "";

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/offers/${offerId}`)
      .then(({ data }) => {
        if (!cancelled) setOffer(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "unknown");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [offerId]);

  // If date is missing (direct URL access to /experience/pass_day), bounce back
  // to the Beach Club calendar so the customer picks a valid weekday first.
  // iter-96 · Exception — Sunset Club runs every Saturday, so the presentation
  // page must render even without ?date=... (a next-Saturday CTA suffices).
  useEffect(() => {
    if (!loading && offer && !dateFromUrl && offerId !== "sunset_club") {
      const t = setTimeout(() => navigate("/univers/beach-club", { replace: true }), 100);
      return () => clearTimeout(t);
    }
  }, [loading, offer, dateFromUrl, navigate, offerId]);

  const bookingHref = useMemo(() => {
    const qs = dateFromUrl ? `?date=${dateFromUrl}` : "";
    return `/booking/${offerId}${qs}`;
  }, [offerId, dateFromUrl]);

  if (loading) {
    return (
      <div
        className="min-h-screen bg-white flex items-center justify-center text-[#0A0A0A]/40 text-sm uppercase tracking-[0.3em]"
        data-testid="offer-detail-loading"
      >
        Chargement…
      </div>
    );
  }

  if (error === "not_found" || !offer) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-24 text-center">
        <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45 mb-4">
          404 · Introuvable
        </div>
        <h1 className="font-display-serif text-3xl md:text-4xl mb-4">
          Cette offre n&apos;est plus disponible
        </h1>
        <Link to="/" className="btn-gold inline-flex items-center gap-2 text-[0.72rem]">
          Retour à l&apos;accueil <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const title = offer.name_fr || offer.name_en || offer.id;
  const subtitle = offer.schedule_fr || offer.schedule_en || "";
  const description = offer.tagline_fr || offer.tagline_en || "";
  const image = offer.image_url || FALLBACK_IMAGE[offerId] || FALLBACK_IMAGE.pass_day;
  const usefulInfo = USEFUL_INFO[offerId] || [];
  const grouped = groupUsefulInfo(usefulInfo);

  return (
    <div className="bg-white text-[#0A0A0A] min-h-screen" data-testid="offer-detail-page">
      {/* ── BANNER — image on top, no text overlay ── */}
      <section className="pt-24 md:pt-28 lg:pt-32 px-4 sm:px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-[0.62rem] uppercase tracking-[0.28em] text-[#0A0A0A]/55 hover:text-[#B8922A] mb-5 transition-colors"
            data-testid="offer-back-btn"
          >
            <ChevronLeft size={13} /> Retour
          </button>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full aspect-[16/10] md:aspect-[21/9] bg-[#FAFAF7] overflow-hidden"
            data-testid="offer-banner"
          >
            <img
              src={image}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover object-center"
              loading="eager"
            />
          </motion.div>
        </div>
      </section>

      {/* ── EDITORIAL CONTENT — all info below the banner ── */}
      <section className="py-10 md:py-14 px-4 sm:px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mx-auto">
          {/* Title block */}
          <div className="mb-10 md:mb-12">
            <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 bg-[#B8922A] text-white text-[0.6rem] uppercase tracking-[0.32em] font-medium">
              <Sparkles size={11} /> Expérience Beach Club
            </div>
            <h1
              className="font-display-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#0A0A0A] tracking-tight leading-[1.05] mb-3 break-words"
              data-testid="offer-title"
            >
              {title}
            </h1>
            {subtitle && (
              <p
                className="text-[#B8922A] text-base md:text-xl mb-5 font-light"
                data-testid="offer-subtitle"
              >
                {subtitle}
              </p>
            )}
            <div className="h-px w-16 bg-[#D4B256] mb-5" />
            {description && (
              <p
                className="text-[#0A0A0A]/75 text-[0.98rem] md:text-lg leading-relaxed font-light whitespace-pre-line"
                data-testid="offer-description"
              >
                {description}
              </p>
            )}
            {dateFromUrl && (
              <div
                className="mt-6 inline-flex items-center gap-2 text-[0.82rem] text-[#0A0A0A]/70 capitalize"
                data-testid="offer-selected-date"
              >
                <Calendar size={14} className="text-[#B8922A]" />
                {fmtDateFR(dateFromUrl)}
              </div>
            )}
          </div>

          {/* Informations utiles */}
          {grouped.info.length > 0 && (
            <SectionBlock
              label="Informations utiles"
              icon={<Info size={11} />}
              testid="offer-useful-info"
            >
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                {grouped.info.map((row) => (
                  <div key={row.label} className="border-b border-[#0A0A0A]/8 pb-4">
                    <div className="text-[0.65rem] tracking-[0.28em] uppercase text-[#0A0A0A]/45 mb-1.5">
                      {row.label}
                    </div>
                    <div className="text-sm text-[#0A0A0A]/85 leading-relaxed">
                      {row.value}
                    </div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          )}

          {/* Inclus */}
          {grouped.inclus.length > 0 && (
            <SectionBlock label="Inclus" icon={<Sparkles size={11} />} testid="offer-inclus">
              <ul className="space-y-2.5 text-[0.95rem] text-[#0A0A0A]/85 leading-relaxed">
                {grouped.inclus.map((row) => (
                  <li key={row.label} className="flex gap-3">
                    <span className="text-[#B8922A] mt-1">·</span>
                    <span>{row.value}</span>
                  </li>
                ))}
              </ul>
            </SectionBlock>
          )}

          {/* Conditions */}
          {grouped.conditions.length > 0 && (
            <SectionBlock
              label="Conditions"
              icon={<Info size={11} />}
              testid="offer-conditions"
            >
              <ul className="space-y-2.5 text-[0.95rem] text-[#0A0A0A]/85 leading-relaxed">
                {grouped.conditions.map((row) => (
                  <li key={row.label} className="flex gap-3">
                    <span className="text-[#B8922A] mt-1">·</span>
                    <span>{row.value}</span>
                  </li>
                ))}
              </ul>
            </SectionBlock>
          )}

          {/* Tarifs & capacité */}
          <SectionBlock
            label="Tarifs & disponibilités"
            icon={<Users size={11} />}
            testid="offer-tariffs"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-[#FAF7F2] border border-[#0A0A0A]/8 p-5">
                <div className="text-[0.6rem] tracking-[0.28em] uppercase text-[#0A0A0A]/45 mb-3">
                  Tarifs
                </div>
                <ul className="text-sm text-[#0A0A0A]/85 space-y-1.5">
                  {typeof offer.price_adult === "number" && offer.price_adult > 0 && (
                    <li data-testid="offer-price-adult">
                      <strong>{fmtXOF(offer.price_adult)}</strong> / Adulte
                    </li>
                  )}
                  {typeof offer.price_child === "number" && offer.price_child > 0 && (
                    <li data-testid="offer-price-child">
                      <strong>{fmtXOF(offer.price_child)}</strong> / Enfant
                    </li>
                  )}
                  {(!offer.price_adult && !offer.price_child) && (
                    <li className="text-[#0A0A0A]/55">Sur demande</li>
                  )}
                </ul>
              </div>
              <div className="bg-[#FAF7F2] border border-[#0A0A0A]/8 p-5">
                <div className="text-[0.6rem] tracking-[0.28em] uppercase text-[#0A0A0A]/45 mb-3">
                  Traversée & capacité
                </div>
                <ul className="text-sm text-[#0A0A0A]/85 space-y-1.5">
                  {Array.isArray(offer.boat_times) && offer.boat_times.length > 0 && (
                    <li className="inline-flex items-center gap-1.5">
                      <Clock size={12} className="text-[#B8922A]" />
                      {offer.boat_times.length} départs / jour
                    </li>
                  )}
                  {typeof offer.max_capacity === "number" && offer.max_capacity > 0 && (
                    <li className="inline-flex items-center gap-1.5">
                      <Users size={12} className="text-[#B8922A]" />
                      Capacité {offer.max_capacity}
                    </li>
                  )}
                  {(!offer.boat_times?.length && !offer.max_capacity) && (
                    <li className="text-[#0A0A0A]/55">Informations à confirmer sur place</li>
                  )}
                </ul>
              </div>
            </div>
          </SectionBlock>

          {/* CTA */}
          <div className="mt-10 md:mt-14 text-center">
            <Link
              to={bookingHref}
              onClick={() => trackEvent("offer_cta_book", { offer: offerId, date: dateFromUrl })}
              className="btn-gold inline-flex items-center gap-2 text-[0.72rem]"
              data-testid="offer-cta-book"
            >
              Réserver ma place <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
